import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Flag, MessageSquare, HelpCircle, MessageCircle, FileCheck2, CheckCircle2,
  PencilLine, ListChecks, Inbox, Paperclip, Sparkles, XCircle, AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '../api/client';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { ETAPA_MAP } from '../lib/constants';

const TIPO_META = {
  criacao:               { icon: Sparkles,     cor: '#16a34a', label: 'Cadastro' },
  etapa:                 { icon: Flag,         cor: '#2563eb', label: 'Etapa' },
  pergunta:              { icon: HelpCircle,   cor: '#d97706', label: 'Pergunta' },
  resposta:              { icon: MessageCircle,cor: '#0891b2', label: 'Resposta' },
  mensagem:              { icon: MessageSquare,cor: '#64748b', label: 'Mensagem' },
  aprovacao_criada:      { icon: FileCheck2,   cor: '#7c3aed', label: 'Aprovação enviada' },
  aprovacao_aprovada:    { icon: CheckCircle2, cor: '#16a34a', label: 'Aprovação aprovada' },
  aprovacao_alteracao:   { icon: PencilLine,   cor: '#d97706', label: 'Pedido de alteração' },
  aprovacao_cancelada:   { icon: XCircle,      cor: '#64748b', label: 'Aprovação cancelada' },
  aprovacao_expirada:    { icon: AlertCircle,  cor: '#64748b', label: 'Aprovação expirada' },
  tarefa_criada:         { icon: ListChecks,   cor: '#7c3aed', label: 'Tarefa criada' },
  tarefa_concluida:      { icon: CheckCircle2, cor: '#16a34a', label: 'Tarefa concluída' },
  pedido_info_criado:    { icon: Inbox,        cor: '#d97706', label: 'Pedido de informação' },
  pedido_info_resolvido: { icon: CheckCircle2, cor: '#16a34a', label: 'Pedido respondido' },
  pedido_info_cancelado: { icon: XCircle,      cor: '#64748b', label: 'Pedido cancelado' },
  item_resolvido:        { icon: CheckCircle2, cor: '#16a34a', label: 'Item respondido' },
  anexo:                 { icon: Paperclip,    cor: '#64748b', label: 'Anexo' },
  alteracao:             { icon: PencilLine,   cor: '#0891b2', label: 'Alteração' },
};

// Labels amigáveis pros nomes técnicos de coluna
const CAMPO_LABEL = {
  titulo: 'Título',
  objetivo: 'Objetivo',
  publico_alvo: 'Público-alvo',
  mensagem_chave: 'Mensagem-chave',
  data_pedido: 'Data do pedido',
  data_acao: 'Data da ação (início)',
  data_acao_fim: 'Data da ação (fim)',
  data_postagem: 'Data de postagem',
  canais: 'Canais',
  tipos: 'Tipos de conteúdo',
  tags: 'Tags',
  meio_instagram: 'Instagram',
  meio_portais: 'Portais',
  meio_radio: 'Rádio',
  meio_tv: 'TV',
  detalhes: 'Detalhes',
  observacoes: 'Observações',
  arquivo_url: 'Link de arquivo',
  etapa: 'Etapa',
  aprovacao: 'Aprovação',
  responsavel_id: 'Responsável',
  prioridade: 'Prioridade',
};

export function TimelinePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/demandas/${id}/timeline`);
        setData(r);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="loader-full"><span className="spinner" /></div>;
  if (!data?.demanda) return <div className="page">Timeline não encontrada.</div>;

  // Agrupamento por dia
  const grupos = [];
  let diaAtual = null;
  for (const ev of data.eventos) {
    const dia = format(new Date(ev.ts), 'yyyy-MM-dd');
    if (dia !== diaAtual) {
      grupos.push({ dia, eventos: [] });
      diaAtual = dia;
    }
    grupos[grupos.length - 1].eventos.push(ev);
  }

  return (
    <div className="page">
      <button className="btn btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav(`/demandas/${id}`)}>
        <ArrowLeft size={15} /> Voltar para a demanda
      </button>

      <div className="page-header" style={{ marginBottom: 14 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 22 }}>Histórico completo</h1>
          <p className="page-subtitle">
            <Link to={`/demandas/${id}`} style={{ color: 'var(--c-primary)', textDecoration: 'none' }}>
              #{data.demanda.id} · {data.demanda.titulo}
            </Link>
          </p>
        </div>
        <Badge color="info">{data.eventos.length} evento{data.eventos.length === 1 ? '' : 's'}</Badge>
      </div>

      {grupos.length === 0 ? (
        <div className="card"><div className="card-body muted text-sm" style={{ textAlign: 'center', padding: 30 }}>
          Sem eventos registrados.
        </div></div>
      ) : (
        <div className="timeline">
          {grupos.map((g) => (
            <div key={g.dia} className="timeline-group">
              <div className="timeline-date">
                {format(new Date(g.dia + 'T12:00:00'), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </div>
              <div className="timeline-track">
                {g.eventos.map((ev, i) => (
                  <TimelineEvent key={i} ev={ev} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ ev }) {
  const meta = TIPO_META[ev.tipo] || { icon: MessageSquare, cor: '#64748b', label: ev.tipo };
  const Icon = meta.icon;
  const autor = ev.autor;

  return (
    <div className="timeline-event">
      <div className="timeline-dot" style={{ background: meta.cor }}>
        <Icon size={13} color="white" />
      </div>
      <div className="timeline-content">
        <div className="timeline-head">
          <strong className="text-sm">{meta.label}</strong>
          <span className="text-xs muted">{format(new Date(ev.ts), 'HH:mm')}</span>
        </div>
        <div className="timeline-body">
          <EventDetails ev={ev} />
        </div>
        {autor?.nome && (
          <div className="timeline-author">
            <Avatar nome={autor.nome} src={autor.foto} size="sm" />
            <span className="text-xs muted">{autor.nome}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function EventDetails({ ev }) {
  switch (ev.tipo) {
    case 'criacao':
      return <span className="text-sm">Demanda cadastrada no sistema.</span>;
    case 'etapa': {
      const de = ev.data?.de;
      const para = ev.data?.para;
      return (
        <span className="text-sm">
          {de ? (<><Badge color={ETAPA_MAP[de]?.color || 'default'}>{ETAPA_MAP[de]?.label || de}</Badge>{' → '}</>) : 'Etapa inicial: '}
          <Badge color={ETAPA_MAP[para]?.color || 'default'}>{ETAPA_MAP[para]?.label || para}</Badge>
        </span>
      );
    }
    case 'pergunta':
    case 'resposta':
    case 'mensagem':
      return <div className="timeline-quote">{ev.data?.texto}</div>;
    case 'aprovacao_criada':
      return <span className="text-sm">Pedido público criado: <strong>{ev.data?.titulo}</strong></span>;
    case 'aprovacao_aprovada':
      return (
        <div>
          <span className="text-sm">Solicitante aprovou: <strong>{ev.data?.titulo}</strong></span>
          {ev.data?.comentario && <div className="timeline-quote" style={{ marginTop: 6 }}>{ev.data.comentario}</div>}
        </div>
      );
    case 'aprovacao_alteracao':
      return (
        <div>
          <span className="text-sm">Solicitante pediu alteração em: <strong>{ev.data?.titulo}</strong></span>
          {ev.data?.comentario && <div className="timeline-quote" style={{ marginTop: 6 }}>{ev.data.comentario}</div>}
        </div>
      );
    case 'aprovacao_cancelada':
    case 'aprovacao_expirada':
      return <span className="text-sm">{ev.data?.titulo}</span>;
    case 'tarefa_criada':
      return (
        <span className="text-sm">
          {ev.data?.titulo} {ev.data?.responsavel && <em className="muted">· para {ev.data.responsavel}</em>}
        </span>
      );
    case 'tarefa_concluida':
      return <span className="text-sm">{ev.data?.titulo}</span>;
    case 'pedido_info_criado':
    case 'pedido_info_resolvido':
    case 'pedido_info_cancelado':
      return <span className="text-sm">{ev.data?.titulo}</span>;
    case 'item_resolvido':
      return <div className="timeline-quote">{ev.data?.texto}</div>;
    case 'anexo': {
      const isImagem = ev.data?.mime?.startsWith('image/');
      const conteudo = (
        <span className="text-sm">
          {ev.data?.nome} <em className="muted">· {ev.data?.mime}{ev.data?.tamanho ? ` · ${(ev.data.tamanho / 1024).toFixed(0)} KB` : ''}</em>
        </span>
      );
      if (!ev.data?.url) return conteudo;
      return (
        <a href={ev.data.url} target="_blank" rel="noreferrer" style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>
          {conteudo}
          {isImagem && (
            <img src={ev.data.url} alt={ev.data?.nome} style={{ marginTop: 6, maxWidth: 220, maxHeight: 160, borderRadius: 6, border: '1px solid var(--c-border)' }} />
          )}
        </a>
      );
    }
    case 'alteracao': {
      const campo = ev.data?.campo;
      const label = CAMPO_LABEL[campo] || campo;
      const de = ev.data?.de;
      const para = ev.data?.para;
      return (
        <span className="text-sm">
          Alterou <strong>{label}</strong>:{' '}
          <em className="muted">{de || '(vazio)'}</em>
          {' → '}
          <strong>{para || '(vazio)'}</strong>
        </span>
      );
    }
    default:
      return null;
  }
}
